import { useCallback, useState } from "react";
import useAuth from "../../Hooks/useAuth";
import {
  useRequestTaskUploadMutation,
  useConfirmTaskUploadMutation,
  useLazyGetTaskDownloadUrlQuery,
} from "../../store";
import { MAX_ATTACHMENT_SIZE, BLOCKED_EXTENSIONS } from "./taskConstants";

export const TOO_BIG_MESSAGE = "Dosya çok büyük (en fazla 25 MB).";
export const BLOCKED_MESSAGE = "Bu dosya türü yüklenemez.";

// Sondaki nokta/boşlukları at, son uzantıyı al (server ile aynı kural)
export const getExtension = (name) => {
  const clean = String(name || "").replace(/[. ]+$/, "");
  const idx = clean.lastIndexOf(".");
  return idx === -1 ? "" : clean.slice(idx + 1).toLowerCase();
};

export const validateFile = (file) => {
  if (!file) return "Dosya seçilmedi.";
  if (file.size > MAX_ATTACHMENT_SIZE) return TOO_BIG_MESSAGE;
  if (BLOCKED_EXTENSIONS.includes(getExtension(file.name))) return BLOCKED_MESSAGE;
  return null;
};

const putFile = (url, file, headers, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error("Dosya yüklenemedi."));
    xhr.onerror = () => reject(new Error("Dosya yüklenemedi."));
    xhr.onabort = () => reject(new Error("Yükleme iptal edildi."));
    xhr.send(file);
  });

const errMessage = (e) => e?.data?.message || e?.message || "Dosya yüklenemedi.";

function useAttachmentUpload(taskId) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const [requestUpload] = useRequestTaskUploadMutation();
  const [confirm] = useConfirmTaskUploadMutation();
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(
    async (file, { onProgress } = {}) => {
      const invalid = validateFile(file);
      if (invalid) throw new Error(invalid);
      setUploading(true);
      try {
        const mimeType = file.type || "application/octet-stream";
        let req;
        try {
          req = await requestUpload({ token, task: taskId, name: file.name, mimeType, size: file.size }).unwrap();
        } catch (e) {
          throw new Error(errMessage(e));
        }
        const { attachmentId, uploadUrl, headers } = req?.data || req || {};
        await putFile(uploadUrl, file, headers, onProgress);
        let res;
        try {
          res = await confirm({ token, task: taskId, attachmentId }).unwrap();
        } catch (e) {
          throw new Error(errMessage(e));
        }
        return res?.data?.attachment || res?.attachment;
      } finally {
        setUploading(false);
      }
    },
    [requestUpload, confirm, token, taskId]
  );

  return { upload, uploading };
}

// RichTextView/RichTextEditor resolveImage'i: attachmentId -> taze inline görüntüleme linki
export function useTaskImageResolver() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const [getUrl] = useLazyGetTaskDownloadUrlQuery();
  return useCallback(
    async (attachmentId) => {
      const res = await getUrl({ token, attachmentId, inline: true });
      return res?.data?.data?.url || res?.data?.url || null;
    },
    [getUrl, token]
  );
}

export default useAttachmentUpload;
